import type {
    SerializedEditorState,
    SerializedTextNode,
    SerializedParagraphNode,
    SerializedLineBreakNode,
} from 'lexical'
import type { ParagraphDescendant, RichTextParagraph } from '../../../../shared/domain/richText/types.no-deps'
import { isMentionNode, isNewLineNode, isTextNode } from '../../../../shared/domain/richText/helpers.no-deps'
import { type SerializedMentionNode, isSerializedMentionNode } from '../nodes/MentionNode'
import { identity } from '../../../../shared/lib/functional/function.no-deps'
import { isSerializedTextNode } from '../nodes/TextNode'
import { isSerializedLineBreakNode } from '../nodes/LinebreakNode'

type SerializedLeafNode = SerializedMentionNode | SerializedTextNode | SerializedLineBreakNode

function leafFromSlate(descendant: ParagraphDescendant): SerializedLeafNode | null {
    if (isTextNode(descendant)) {
        return {
            detail: 0,
            format: 0,
            mode: 'normal',
            style: '',
            text: descendant.text,
            type: 'text',
            version: 1,
        }
    }

    if (isMentionNode(descendant)) {
        return {
            type: 'mention',
            version: 0,
            userId: descendant.userId,
        }
    }

    if (isNewLineNode(descendant)) {
        return {
            type: 'linebreak',
            version: 1,
        }
    }

    return null
}

function paragraphFromSlate(paragraph: RichTextParagraph): SerializedParagraphNode {
    return {
        type: 'paragraph',
        direction: 'ltr',
        format: '',
        indent: 0,
        version: 1,
        children: paragraph.children.map(leafFromSlate).filter(identity),
    }
}

export function fromSlate(paragraphs: RichTextParagraph[]): SerializedEditorState {
    return {
        root: {
            direction: 'ltr',
            format: '',
            indent: 0,
            type: 'root',
            version: 1,
            children: paragraphs.map(paragraphFromSlate),
        },
    }
}

function leafToSlate(leaf: SerializedLeafNode): ParagraphDescendant | null {
    if (isSerializedTextNode(leaf)) {
        return { text: leaf.text }
    }

    if (isSerializedMentionNode(leaf)) {
        return {
            type: 'mention',
            userId: leaf.userId,
            children: [{ text: '' }],
        }
    }

    if (isSerializedLineBreakNode(leaf)) {
        return {
            type: 'new-line',
            children: [{ text: '' }],
        }
    }
    return null
}

function paragraphToSlate(paragraph: SerializedParagraphNode): RichTextParagraph {
    return { children: paragraph.children.map(leafToSlate).filter(identity) }
}

export function toSlate(state: SerializedEditorState): RichTextParagraph[] {
    return state.root.children.map(paragraphToSlate)
}
